import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { scheduledChecksApi, agentsApi } from "../services/api";
import { useNotification } from "../context/NotificationContext";

export const INTERVAL_OPTIONS = [
  { value: 1,   label: "כל שעה" },
  { value: 6,   label: "כל 6 שעות" },
  { value: 12,  label: "כל 12 שעות" },
  { value: 24,  label: "יומי (24ש)" },
  { value: 48,  label: "כל יומיים" },
  { value: 168, label: "שבועי" },
];

const EMPTY_FORM = { agent_id: "", name: "", description: "", interval_hours: 24, is_enabled: true };
const ENABLED_FILTER_KEYS = new Set(["enabled", "disabled"]);

// Every AgentRunStatus of the backend lands in a bucket: a status the function did not know used to fall through
// to "ok" (green "last run succeeded"), e.g. a Stopped (paused) run.
const RUN_FAILED  = new Set(["failed", "rejected"]);
const RUN_WAITING = new Set(["awaiting_technician_review", "awaiting_approval", "paused", "waiting_for_customer_connection", "waiting_for_customer_answer"]);
const RUN_RUNNING = new Set(["pending", "analyzing", "diagnosing", "connection_active", "approved", "executing"]);
// Waiting on someone OTHER than a reviewer, possibly forever (Stop, or a customer who never answers): these must
// not hide a check whose next run is long past.
const RUN_STALLABLE = new Set(["paused", "waiting_for_customer_connection", "waiting_for_customer_answer"]);

export function scheduleHealth(c) {
  if (!c.is_enabled) return "paused";
  const st = c.latest_run_status;
  if (!st) return "never";
  if (RUN_FAILED.has(st)) return "failed";
  const nextRunLate = Boolean(c.next_run_at) && new Date(c.next_run_at) < new Date(Date.now() - 30 * 60 * 1000);
  if (RUN_STALLABLE.has(st)) return nextRunLate ? "overdue" : "pending";
  if (RUN_WAITING.has(st)) return "pending";
  if (RUN_RUNNING.has(st)) return "running";
  if (nextRunLate) return "overdue";
  return "ok";
}

export const HEALTH_DOT = {
  ok:      { cls: "bg-green-500",  title: "ריצה אחרונה הצליחה" },
  running: { cls: "bg-blue-400 animate-pulse", title: "ריצה בתהליך" },
  pending: { cls: "bg-amber-400",  title: "ממתין לפעולה (סקירה / לקוח / הריצה נעצרה)" },
  overdue: { cls: "bg-orange-500", title: "מאחרת — הרצה הבאה עברה" },
  failed:  { cls: "bg-red-500",    title: "ריצה אחרונה נכשלה" },
  never:   { cls: "bg-gray-300",   title: "לא רצה עדיין" },
  paused:  { cls: "bg-gray-200",   title: "מושהה" },
};
export const RUN_STATUS = {
  pending: "מאתחל",
  analyzing: "מנתח",
  awaiting_technician_review: "ממתין לסקירה",
  awaiting_approval: "ממתין לאישור",
  completed: "הושלם",
  resolved: "נפתר",
  rejected: "נדחה",
  failed: "נכשל",
  paused: "הריצה נעצרה",
  approved: "אושר",
  executing: "מבצע",
  diagnosing: "מאבחן",
  connection_active: "חיבור פעיל",
  waiting_for_customer_connection: "ממתין לחיבור הלקוח",
  waiting_for_customer_answer: "ממתין לתשובת הלקוח",
};

// ─── Shared operational-state vocabulary ─────────────────────────────────────
// The scheduled-operations and guardian surfaces import these so the same class of state reads
// the same way everywhere: a fetch failure is named by kind, a past timestamp gets a bounded age
// label, and a run status gets a tone. No surface may upgrade an unknown state to a good one.

// 403 → a permissions problem, an error with no server response → the server could not be
// reached (offline), anything else → a failed load. Callers must degrade per kind and never
// render any of them as "empty" or "healthy".
export function fetchFailureKind(error) {
  if (error?.response?.status === 403) return "forbidden";
  if (error != null && !error.response) return "offline";
  return "failed";
}

export const FETCH_FAILURE_TEXT = {
  forbidden: "אין הרשאה לצפות בנתונים (נדרשת הרשאה מתאימה) — לא ניתן לקבוע את המצב.",
  offline: "לא התקבלה תשובה מהשרת (ייתכן שאין חיבור) — לא ניתן לקבוע את המצב.",
  failed: "טעינת הנתונים נכשלה — לא ניתן לקבוע את המצב.",
};

// Age of a past timestamp, or null when it is unknown/unparseable — surfaces say "unknown"
// rather than guessing.
export function formatAgeHe(targetIso, now) {
  const target = Date.parse(targetIso || "");
  if (Number.isNaN(target)) return null;
  const minutes = Math.floor((now - target) / 60_000);
  if (minutes < 1) return "פחות מדקה";
  if (minutes < 60) return minutes === 1 ? "דקה" : `${minutes} דקות`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return hours === 1 ? "שעה" : `${hours} שעות`;
  const days = Math.floor(hours / 24);
  return days === 1 ? "יום" : `${days} ימים`;
}

// A run status reads at a glance: failure red, in-flight blue, waiting-on-someone (review,
// approval, customer, stop) amber, success green; an unrecognized status stays neutral instead
// of wearing a success color.
export function runStatusTone(status) {
  if (RUN_FAILED.has(status)) return "text-red-600";
  if (RUN_RUNNING.has(status)) return "text-blue-600";
  if (RUN_WAITING.has(status)) return "text-amber-700";
  if (status === "completed" || status === "resolved") return "text-emerald-700";
  return "text-gray-500";
}

function fmt(dt) {
  if (!dt) return "—";
  return new Date(dt).toLocaleString("he-IL", { dateStyle: "short", timeStyle: "short" });
}

export default function ScheduledChecksPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const qc = useQueryClient();
  const { addNotification } = useNotification();
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [editId, setEditId] = useState(null);
  const [editForm, setEditForm] = useState({ name: "", description: "", interval_hours: 24 });
  const [runningId, setRunningId] = useState(null);
  const [healthFilter, setHealthFilter] = useState(null);
  const [now, setNow] = useState(() => Date.now());
  const enabledFilter = ENABLED_FILTER_KEYS.has(searchParams.get("filter")) ? searchParams.get("filter") : "all";

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 60_000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    const rawFilter = searchParams.get("filter");
    if (rawFilter && !ENABLED_FILTER_KEYS.has(rawFilter)) {
      setSearchParams((previous) => {
        const next = new URLSearchParams(previous);
        next.delete("filter");
        return next;
      }, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const {
    data: checks = [],
    isLoading,
    isError: checksFailed,
    error: checksError,
    refetch: refetchChecks,
  } = useQuery({
    queryKey: ["scheduled-checks"],
    queryFn: scheduledChecksApi.list,
    refetchInterval: 30000,
  });

  const { data: agents = [] } = useQuery({
    queryKey: ["agents"],
    queryFn: () => agentsApi.list(),
  });

  const enabledAgents = agents.filter((a) => a.is_enabled);
  const visibleChecks = checks.filter((check) => {
    if (enabledFilter !== "all" && (enabledFilter === "enabled" ? !check.is_enabled : check.is_enabled)) return false;
    if (healthFilter && scheduleHealth(check) !== healthFilter) return false;
    return true;
  });
  const overdueCount = checks.filter((c) => scheduleHealth(c) === "overdue").length;
  const failedCount  = checks.filter((c) => scheduleHealth(c) === "failed").length;
  const pendingCount = checks.filter((c) => scheduleHealth(c) === "pending").length;

  const setEnabledFilter = (filter) => {
    setSearchParams((previous) => {
      const next = new URLSearchParams(previous);
      if (filter === "all") {
        next.delete("filter");
      } else {
        next.set("filter", filter);
      }
      return next;
    }, { replace: true });
  };

  const createMutation = useMutation({
    mutationFn: scheduledChecksApi.create,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["scheduled-checks"] });
      setShowForm(false);
      setForm(EMPTY_FORM);
      addNotification("בדיקה מתוזמנת נוצרה בהצלחה", "success");
    },
    onError: (err) => addNotification(err?.response?.data?.detail || "שגיאה ביצירת בדיקה מתוזמנת", "error"),
  });

  const toggleMutation = useMutation({
    mutationFn: ({ id, is_enabled }) => scheduledChecksApi.update(id, { is_enabled }),
    onSuccess: (_, { is_enabled }) => {
      qc.invalidateQueries({ queryKey: ["scheduled-checks"] });
      addNotification(is_enabled ? "בדיקה הופעלה" : "בדיקה הושהתה", "info");
    },
    onError: (err) => addNotification(err?.response?.data?.detail || "שגיאה בשינוי מצב הבדיקה", "error"),
  });

  const deleteMutation = useMutation({
    mutationFn: scheduledChecksApi.remove,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["scheduled-checks"] });
      addNotification("בדיקה מתוזמנת נמחקה", "info");
    },
    onError: (err) => addNotification(err?.response?.data?.detail || "שגיאה במחיקת הבדיקה", "error"),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, ...payload }) => scheduledChecksApi.update(id, payload),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: ["scheduled-checks"] });
      // Only close the edit form if it still belongs to the check just saved -- the user may
      // have switched to editing a different check while this request was in flight (editId is
      // a single page-wide slot), and closing unconditionally silently discarded that unrelated,
      // unsaved edit. Same fix as TechniciansPage.jsx's identical race.
      setEditId((current) => (current === variables.id ? null : current));
      addNotification("בדיקה עודכנה בהצלחה", "success");
    },
    onError: (err) => addNotification(err?.response?.data?.detail || "שגיאה בעדכון הבדיקה", "error"),
  });

  const runNowMutation = useMutation({
    mutationFn: scheduledChecksApi.runNow,
    onSuccess: (result, id) => {
      // Only clear the slot if it still belongs to the run just resolved -- runningId is a
      // single page-wide slot, so triggering a second check's run while the first is still in
      // flight otherwise let either resolution wipe the OTHER row's "running" indicator early,
      // re-enabling its button before that request actually finished. Same fix as this file's
      // own updateMutation.onSuccess (editId) a few lines above.
      setRunningId((current) => (current === id ? null : current));
      qc.invalidateQueries({ queryKey: ["scheduled-checks"] });
      if (result?.run_id) navigate(`/agent-runs/${result.run_id}`);
    },
    onError: (err, id) => {
      setRunningId((current) => (current === id ? null : current));
      addNotification(err?.response?.data?.detail || "שגיאה בהפעלת הבדיקה", "error");
    },
  });

  const handleCreate = (e) => {
    e.preventDefault();
    createMutation.mutate({
      ...form,
      interval_hours: parseInt(form.interval_hours, 10),
    });
  };

  const handleUpdate = (e) => {
    e.preventDefault();
    updateMutation.mutate({
      id: editId,
      name: editForm.name,
      description: editForm.description || null,
      interval_hours: parseInt(editForm.interval_hours, 10),
    });
  };

  return (
    <div className="max-w-5xl mx-auto space-y-5" dir="rtl">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-800">בדיקות מתוזמנות</h1>
        <button
          onClick={() => { setShowForm(!showForm); setEditId(null); }}
          className="bg-brand-600 hover:bg-brand-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
        >
          + מתוזמן חדש
        </button>
      </div>

      {/* A refresh that fails after a successful load is a stale view, not a fresh one and not an error screen. */}
      {checksFailed && checks.length > 0 && (
        <div role="status" className="flex items-center justify-between gap-2 flex-wrap text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-2">
          <span>הרענון האחרון של רשימת הבדיקות נכשל — הנתונים המוצגים הם מהטעינה האחרונה שהצליחה.</span>
          <button type="button" onClick={() => refetchChecks()} className="text-brand-600 hover:underline shrink-0">
            נסה שוב
          </button>
        </div>
      )}

      {/* Create form */}
      {showForm && (
        <form onSubmit={handleCreate} className="bg-white rounded-xl shadow-sm border p-6 space-y-4">
          <h2 className="font-semibold text-gray-700 border-b pb-2">בדיקה מתוזמנת חדשה</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs text-gray-500 mb-1">סוכן</label>
              <select
                required
                value={form.agent_id}
                onChange={(e) => setForm((f) => ({ ...f, agent_id: e.target.value }))}
                className="w-full border rounded-lg px-3 py-2 text-sm"
              >
                <option value="">בחר סוכן...</option>
                {enabledAgents.map((a) => (
                  <option key={a.id} value={a.id}>{a.name}</option>
                ))}
                {enabledAgents.length === 0 && (
                  <option disabled>אין סוכנים פעילים — הפעל סוכן קודם</option>
                )}
              </select>
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">שם הבדיקה</label>
              <input
                required
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                placeholder="בדיקת בריאות שרתים"
                className="w-full border rounded-lg px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">תדירות</label>
              <select
                value={form.interval_hours}
                onChange={(e) => setForm((f) => ({ ...f, interval_hours: parseInt(e.target.value, 10) }))}
                className="w-full border rounded-lg px-3 py-2 text-sm"
              >
                {INTERVAL_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
            <div className="flex items-center gap-2 pt-5">
              <input
                type="checkbox"
                id="is_enabled"
                checked={form.is_enabled}
                onChange={(e) => setForm((f) => ({ ...f, is_enabled: e.target.checked }))}
                className="rounded"
              />
              <label htmlFor="is_enabled" className="text-sm text-gray-700">פעיל מיד</label>
            </div>
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">תיאור (אופציונלי)</label>
            <input
              value={form.description}
              onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
              placeholder="בדיקה תקופתית של..."
              className="w-full border rounded-lg px-3 py-2 text-sm"
            />
          </div>
          {createMutation.isError && (
            <p className="text-red-600 text-sm">{createMutation.error?.response?.data?.detail}</p>
          )}
          <div className="flex gap-2">
            <button type="submit" disabled={createMutation.isPending}
              className="bg-brand-600 text-white px-5 py-2 rounded-lg text-sm font-medium disabled:opacity-50">
              {createMutation.isPending ? "יוצר..." : "צור בדיקה"}
            </button>
            <button type="button" onClick={() => { setShowForm(false); setForm(EMPTY_FORM); }}
              className="border px-5 py-2 rounded-lg text-sm hover:bg-gray-50">
              ביטול
            </button>
          </div>
        </form>
      )}

      {/* Edit form */}
      {editId && (
        <form onSubmit={handleUpdate} className="bg-white rounded-xl shadow-sm border p-6 space-y-4">
          <h2 className="font-semibold text-gray-700 border-b pb-2">
            עריכת בדיקה: {checks.find((c) => c.id === editId)?.name}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs text-gray-500 mb-1">שם הבדיקה</label>
              <input
                required
                value={editForm.name}
                onChange={(e) => setEditForm((f) => ({ ...f, name: e.target.value }))}
                className="w-full border rounded-lg px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">תדירות</label>
              <select
                value={editForm.interval_hours}
                onChange={(e) => setEditForm((f) => ({ ...f, interval_hours: parseInt(e.target.value, 10) }))}
                className="w-full border rounded-lg px-3 py-2 text-sm"
              >
                {INTERVAL_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">תיאור (אופציונלי)</label>
            <input
              value={editForm.description}
              onChange={(e) => setEditForm((f) => ({ ...f, description: e.target.value }))}
              className="w-full border rounded-lg px-3 py-2 text-sm"
            />
          </div>
          <div className="flex gap-2">
            <button type="submit" disabled={updateMutation.isPending}
              className="bg-brand-600 text-white px-5 py-2 rounded-lg text-sm font-medium disabled:opacity-50">
              {updateMutation.isPending ? "שומר..." : "שמור שינויים"}
            </button>
            <button type="button" onClick={() => setEditId(null)}
              className="border px-5 py-2 rounded-lg text-sm hover:bg-gray-50">
              ביטול
            </button>
          </div>
        </form>
      )}

      {/* Health summary — click to filter */}
      {checks.length > 0 && (overdueCount > 0 || failedCount > 0 || pendingCount > 0 || healthFilter) && (
        <div className="flex flex-wrap gap-2 text-xs items-center">
          {overdueCount > 0 && (
            <button
              type="button"
              aria-pressed={healthFilter === "overdue"}
              onClick={() => setHealthFilter((f) => f === "overdue" ? null : "overdue")}
              className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full border transition-colors ${
                healthFilter === "overdue" ? "bg-orange-200 border-orange-400 text-orange-800 font-semibold" : "bg-orange-50 border-orange-200 text-orange-700 hover:bg-orange-100"
              }`}
            >
              <span className="w-2 h-2 rounded-full bg-orange-500 inline-block" />
              {overdueCount} בדיקות מאחרות
            </button>
          )}
          {failedCount > 0 && (
            <button
              type="button"
              aria-pressed={healthFilter === "failed"}
              onClick={() => setHealthFilter((f) => f === "failed" ? null : "failed")}
              className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full border transition-colors ${
                healthFilter === "failed" ? "bg-red-200 border-red-400 text-red-800 font-semibold" : "bg-red-50 border-red-200 text-red-700 hover:bg-red-100"
              }`}
            >
              <span className="w-2 h-2 rounded-full bg-red-500 inline-block" />
              {failedCount} נכשלו
            </button>
          )}
          {pendingCount > 0 && (
            <button
              type="button"
              aria-pressed={healthFilter === "pending"}
              onClick={() => setHealthFilter((f) => f === "pending" ? null : "pending")}
              className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full border transition-colors ${
                healthFilter === "pending" ? "bg-amber-200 border-amber-400 text-amber-800 font-semibold" : "bg-amber-50 border-amber-200 text-amber-700 hover:bg-amber-100"
              }`}
            >
              <span className="w-2 h-2 rounded-full bg-amber-400 inline-block" />
              {pendingCount} ממתינות לפעולה
            </button>
          )}
          {healthFilter && (
            <button
              type="button"
              onClick={() => setHealthFilter(null)}
              className="text-gray-400 hover:text-gray-600 px-1.5 py-1 rounded-full border border-gray-200 hover:border-gray-300 transition-colors"
              title="נקה פילטר"
              aria-label="נקה סינון לפי מצב"
            >✕</button>
          )}
        </div>
      )}

      {/* Table */}
      {checks.length > 0 && (
        <div className="flex items-center justify-between flex-wrap gap-2 text-sm">
          <div className="flex rounded-lg border border-gray-200 overflow-hidden text-xs">
            {[{ key: "all", label: `הכל (${checks.length})` }, { key: "enabled", label: `פעילות (${checks.filter((c) => c.is_enabled).length})` }, { key: "disabled", label: `מושהות (${checks.filter((c) => !c.is_enabled).length})` }].map((filter, index) => (
              <button
                key={filter.key}
                type="button"
                aria-pressed={enabledFilter === filter.key}
                onClick={() => setEnabledFilter(filter.key)}
                className={`px-3 py-1.5 ${index > 0 ? "border-r border-gray-200" : ""} ${enabledFilter === filter.key ? "bg-brand-600 text-white font-medium" : "bg-white text-gray-600 hover:bg-gray-50"}`}
              >
                {filter.label}
              </button>
            ))}
          </div>
          {enabledFilter !== "all" && <span className="text-xs text-gray-400">מציג {visibleChecks.length} מתוך {checks.length}</span>}
        </div>
      )}
      {isLoading ? (
        <div className="flex justify-center py-16">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600" />
        </div>
      ) : checksFailed && checks.length === 0 ? (
        // a failed request is not "no scheduled checks": they exist and may be running. The
        // failure is named by kind — a refusal (admin-only API) is a permission state, an
        // unreachable server is named as such, anything else is a failed load.
        fetchFailureKind(checksError) === "forbidden" ? (
          <div className="bg-white rounded-xl shadow-sm border p-12 text-center text-gray-600">
            <p className="font-medium">אין הרשאה לצפות בבדיקות המתוזמנות</p>
            <p className="text-sm text-gray-400 mt-1">ניהול בדיקות מתוזמנות זמין למנהלים בלבד.</p>
          </div>
        ) : (
          <div role="alert" className="bg-white rounded-xl shadow-sm border p-12 text-center text-red-600">
            <p className="font-medium">לא ניתן לטעון את הבדיקות המתוזמנות</p>
            <p className="text-sm text-gray-500 mt-1">{FETCH_FAILURE_TEXT[fetchFailureKind(checksError)]}</p>
            <p className="text-xs text-gray-400 mt-1">אין להסיק שאין בדיקות מתוזמנות — הן קיימות וייתכן שרצות כעת.</p>
            <button type="button" onClick={() => refetchChecks()} className="mt-2 text-sm text-brand-600 hover:underline">נסה שוב</button>
          </div>
        )
      ) : checks.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm border p-16 text-center text-gray-400">
          <p className="text-3xl mb-3">⏱</p>
          <p>אין בדיקות מתוזמנות</p>
          <p className="text-xs mt-1">צור בדיקה כדי שהסוכן יבצע בדיקות IT אוטומטיות</p>
        </div>
      ) : visibleChecks.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm border p-12 text-center text-gray-400 text-sm">
          אין בדיקות {enabledFilter === "enabled" ? "פעילות" : "מושהות"}
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
          <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b text-gray-500 text-xs uppercase">
              <tr>
                <th className="text-right px-4 py-3">שם הבדיקה</th>
                <th className="text-right px-4 py-3">סוכן</th>
                <th className="text-right px-4 py-3">תדירות</th>
                <th className="text-right px-4 py-3">הרצה אחרונה</th>
                <th className="text-right px-4 py-3">תוצאת ריצה</th>
                <th className="text-right px-4 py-3">הרצה הבאה</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody>
              {visibleChecks.map((c) => {
                const lastRunAge = c.last_run_at ? formatAgeHe(c.last_run_at, now) : null;
                return (
                <tr key={c.id} className="border-b last:border-0 hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      {(() => { const h = HEALTH_DOT[scheduleHealth(c)]; return <span className={`w-2 h-2 rounded-full flex-shrink-0 ${h.cls}`} title={h.title} />; })()}
                      <div>
                        <div className="font-medium text-gray-800">{c.name}</div>
                        {c.description && (
                          <div className="text-xs text-gray-400">{c.description}</div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <Link to={`/agents/${c.agent_id}`} className="text-brand-600 hover:underline text-xs">
                      {c.agent_name}
                    </Link>
                  </td>
                  <td className="px-4 py-3 text-gray-600">
                    {INTERVAL_OPTIONS.find((o) => o.value === c.interval_hours)?.label || `${c.interval_hours}ש`}
                  </td>
                  <td className="px-4 py-3 text-gray-500 text-xs">
                    {fmt(c.last_run_at)}
                    {lastRunAge && <div className="text-gray-400">לפני {lastRunAge}</div>}
                  </td>
                  <td className="px-4 py-3 text-xs">
                    {c.latest_run_id ? (
                      <Link to={`/agent-runs/${c.latest_run_id}`} className={`hover:underline ${runStatusTone(c.latest_run_status)}`}>
                        {RUN_STATUS[c.latest_run_status] || c.latest_run_status || "ריצה"}
                      </Link>
                    ) : <span className="text-gray-400">טרם הורץ</span>}
                  </td>
                  <td className="px-4 py-3 text-xs">
                    {c.is_enabled ? (
                      <span className="text-gray-600">{fmt(c.next_run_at)}</span>
                    ) : (
                      <span className="text-gray-400">מושהה</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3 justify-end">
                      {/* Toggle enabled */}
                      <button
                        onClick={() => toggleMutation.mutate({ id: c.id, is_enabled: !c.is_enabled })}
                        aria-label={`${c.is_enabled ? "פעיל" : "מושהה"}, בדיקה: ${c.name}`}
                        className={`text-xs px-2 py-1 rounded-full font-medium transition-colors ${
                          c.is_enabled
                            ? "bg-green-100 text-green-700 hover:bg-green-200"
                            : "bg-gray-100 text-gray-500 hover:bg-gray-200"
                        }`}
                      >
                        {c.is_enabled ? "פעיל" : "מושהה"}
                      </button>
                      {/* Run now */}
                      <button
                        onClick={() => {
                          setRunningId(c.id);
                          runNowMutation.mutate(c.id);
                        }}
                        disabled={runningId === c.id}
                        aria-label={`הפעל עכשיו, בדיקה: ${c.name}`}
                        className="text-xs text-indigo-600 hover:underline disabled:opacity-50"
                      >
                        {runningId === c.id ? "מפעיל..." : "▶ הפעל"}
                      </button>
                      {/* Edit */}
                      <button
                        onClick={() => {
                          setEditId(c.id);
                          setEditForm({ name: c.name, description: c.description || "", interval_hours: c.interval_hours });
                          setShowForm(false);
                        }}
                        aria-label={`ערוך, בדיקה: ${c.name}`}
                        className="text-xs text-gray-500 hover:underline"
                      >
                        ערוך
                      </button>
                      {/* Delete */}
                      <button
                        onClick={() => {
                          if (window.confirm(`למחוק את "${c.name}"?`)) {
                            deleteMutation.mutate(c.id);
                          }
                        }}
                        disabled={deleteMutation.isPending && deleteMutation.variables === c.id}
                        aria-label={`מחק, בדיקה: ${c.name}`}
                        className="text-xs text-red-500 hover:underline disabled:opacity-50"
                      >
                        מחק
                      </button>
                    </div>
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
          </div>
        </div>
      )}

      <p className="text-xs text-gray-400 text-center">
        הבדיקות מופעלות אוטומטית ע"י הסוכן המשויך · תוצאות ניתנות לצפייה ב
        <Link to="/agent-runs" className="text-brand-600 hover:underline mx-1">ריצות סוכן</Link>
      </p>
    </div>
  );
}
